import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CL_PREAUTH_GIFTING_PARAM,
  CL_PREAUTH_PRE_SELECTED_PLAN_PARAM,
  CL_PREAUTH_ROUTER_STATE_PARAM,
  CL_PREAUTH_SECOND_SCREEN_PARAM,
  CL_PREAUTH_URL_SEARCH_PARAM,
  NEW_USER_DATE_CREATED_DURATION,
  VA_QUERY_PARAM,
} from 'Utils/Constants';
import { handleSignInSuccess, handleSignUpSuccess, signInRedirect } from 'Utils/SignInHelpers';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { createPaymentAccount as checkoutCreatePaymentAccount } from 'State/Checkout';
import { useStore } from 'Hooks/useStore';
import { hideOverlay, showOverlay } from 'State/Layout';
import { addRedirectUrl, setInitialStep } from 'State/Integrations';
import { PreAuthParams } from 'Utils/PreAuthParams';
import { Loader } from 'Components/Loader';
import { centralizedLoginSDK } from 'Utils/CentralizedLoginSDK';
import { profileSDK } from 'Utils/ProfileSDK';
import { setBrazeUserLoggedIn } from 'Utils/Braze';
import { setSecondScreen } from 'State/SecondScreen';
import { signOut } from 'State/User';
import { getUserInfo } from 'Api/ProfileApi';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { getPlans } from 'State/Plans';
import { fetchEventState } from 'State/PurchaseMode';

const LOGIN_OVERLAY_MSG = 'Signing in to your account...';

export const CallbackComponent = () => {
  const {
    bango: { subscriptionId },
  } = useStore('integrations');
  const { routes } = useStore('appConfig');
  const secondScreenState = useStore('secondScreen');
  const userState = useStore('user');

  const location = useLocation();
  const navigate = useNavigate();

  const [redirectOptions, setRedirectOptions] = useState({
    path: null,
    search: null,
    state: {},
  });
  const [checkedForPreselectedPlan, setCheckedForPreselectedPlan] = useState(false);

  const isFirstTime = useRef(true);

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const stateUuid = urlParams.get('state');
  const preAuthParams = useMemo(() => new PreAuthParams(stateUuid), [stateUuid]);
  // retrieve the stored url params (from before redirecting to CL) and pass to signInRedirect helper
  // so that future calls to `location` include the new params

  const storedUrlParamStr = preAuthParams.get(CL_PREAUTH_URL_SEARCH_PARAM);
  const storedRouterState = preAuthParams.get(CL_PREAUTH_ROUTER_STATE_PARAM);
  let updatedLocation = location;
  if (storedUrlParamStr) {
    const newUrlParams = new URLSearchParams(storedUrlParamStr);
    const combinedParams = `${urlParams.toString()}&${newUrlParams.toString()}`;
    const updatedSearch = `?${combinedParams}`;
    // create updated location object for the sign in helpers
    updatedLocation = {
      ...location,
      search: updatedSearch,
    };
  }
  if (storedRouterState) {
    try {
      const parsedState = JSON.parse(storedRouterState);
      updatedLocation = {
        ...updatedLocation,
        state: {
          ...updatedLocation.state,
          ...parsedState,
        },
      };
    } catch (e) {
      console.error('Error parsing stored router state from centralized login pre-auth params', e);
    }
  }

  const preSelectedPlan = preAuthParams.get(CL_PREAUTH_PRE_SELECTED_PLAN_PARAM) ?? '';

  useEffect(() => {
    async function fetchPlansForPreSelectedPlan() {
      if (preSelectedPlan.trim().length > 0) {
        try {
          await getPlans(preSelectedPlan);
        } finally {
          setCheckedForPreselectedPlan(true);
        }
      }
    }

    fetchPlansForPreSelectedPlan();
  }, [preSelectedPlan]);

  useEffect(() => {
    if (isFirstTime.current && (checkedForPreselectedPlan || preSelectedPlan.trim().length < 1)) {
      isFirstTime.current = false;

      let isDeviceActivation = secondScreenState?.isDeviceActivation;
      const persistedSecondScreenState = preAuthParams.get(CL_PREAUTH_SECOND_SCREEN_PARAM);
      if (persistedSecondScreenState) {
        isDeviceActivation = persistedSecondScreenState?.isDeviceActivation;
        setSecondScreen(persistedSecondScreenState);
      }

      const isGiftingFromPreAuth = preAuthParams.get(CL_PREAUTH_GIFTING_PARAM);
      if (isGiftingFromPreAuth) {
        fetchEventState(true);
      }

      let isSignUpFlow = false;

      // both handlers mimic the logic of the flow where there is no central auth and the user were logging in/signing
      // up directly on nation legacy
      const onSignInSuccess = handleSignInSuccess();
      const from = updatedLocation.state?.from;
      const fromPathname =
        typeof from === 'string'
          ? from
          : typeof from === 'object' && from !== null && 'pathname' in from
            ? from.pathname
            : '';
      const isSheerIdVerification = fromPathname.includes(routes.sheerIdVerification);
      const updatedUrlParams = new URLSearchParams(updatedLocation.search);
      const isVeteransAdv = updatedUrlParams.get(VA_QUERY_PARAM);
      const onSignUpSuccess = handleSignUpSuccess(isSheerIdVerification, isVeteransAdv, updatedUrlParams, (path) => {
        navigate(path);
      });

      const initHandleUser = async () => {
        let userData;

        try {
          if (!userState.isAuthenticated) {
            await centralizedLoginSDK().onAuthCallback();
          }

          const cookie = await profileSDK().getUserFromCookie();

          const userInfo = await getUserInfo(cookie?.accessToken);

          userData = {
            ...cookie,
            ...userInfo,
            profileId: cookie?.cpCustomerID ?? userInfo?.profileId,
          };

          if (userData.profileId) {
            profileSDK().setProfileId({ profileId: userData.profileId });
          }

          if (userData.accessToken) {
            profileSDK().setAccessToken({ accessToken: userData.accessToken });
            setBrazeUserLoggedIn();
          }

          if (userData.error) {
            throw new Error(userData.error);
          } else if (Date.now() - new Date(userData.dateCreated) < NEW_USER_DATE_CREATED_DURATION) {
            isSignUpFlow = true;
            await handleSignedUpUser(userData);
          } else {
            await handleSignedInUser(userData);
          }
        } catch (error) {
          const errorMessage =
            error && typeof error === 'object' && 'message' in error && error.message ? error.message : String(error);

          trackEvent(
            isSignUpFlow ? TRACKING_ENUMS.EVENT_SIGNUP_ERROR : TRACKING_ENUMS.EVENT_SIGNIN_ERROR,
            {
              page_error_description: errorMessage,
            },
            {
              dcg_profile_id: userData?.profileId ?? '',
              error_message: errorMessage,
            }
          );

          signOut();
        }
      };

      const handleSignedInUser = async (userData) => {
        if (!isDeviceActivation) showOverlay(LOGIN_OVERLAY_MSG);

        const hasEntitlement = await onSignInSuccess(userData);

        if (subscriptionId) {
          await checkoutCreatePaymentAccount();
        }

        const { isExternalRedirect, redirectPath, redirectSearch, redirectState, redirectStep, redirectUrl } =
          await signInRedirect(hasEntitlement, updatedLocation);
        // if not external redirect, hide the overlay
        if (!isDeviceActivation && !isExternalRedirect) {
          hideOverlay();
        }

        if (redirectStep) setInitialStep(redirectStep);
        if (redirectUrl) addRedirectUrl(redirectUrl);
        setRedirectOptions({
          path: redirectPath,
          search: redirectSearch,
          state: redirectState,
        });
      };

      const handleSignedUpUser = async (userData) => {
        const { accessToken, userType, profileId } = userData;

        if (accessToken) {
          await onSignInSuccess(userData, true);
          const utagData = {
            profile_type: userType,
            newsletter_opt_in: 'yes',
            dcg_profile_id: profileId,
          };

          trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_COMPLETED, {}, utagData);

          return onSignUpSuccess();
        }

        throw new Error('No access token found for newly signed up user');
      };

      initHandleUser();
    }
  }, [
    preAuthParams,
    updatedLocation,
    location,
    subscriptionId,
    navigate,
    routes,
    secondScreenState,
    userState,
    checkedForPreselectedPlan,
    preSelectedPlan,
    urlParams,
  ]);

  if (redirectOptions.path) {
    return (
      <Navigate
        replace
        to={{
          pathname: redirectOptions.path,
          search: redirectOptions.search,
        }}
        state={redirectOptions.state}
      />
    );
  }

  return <Loader />;
};

export const CallbackContainer = CallbackComponent;
